<template>
    <div class="card card-todo p-4" :class="{ 'card-todo-empty-state': isAllColumnsEmpty }">
        <to-do-date-navigation v-if="selectedDate" :date="selectedDate" :role="role" :timezone="userInfo?.timezone" @date-selected="onDateChange" />
        <to-do-user-tabs ref="userTabs" v-if="role === 5 || role === 15" :role-id="role" :user-id="userId" class="mt-2" @user-selected="onUserTabClick" />
        <div v-if="!isProduction && (role === 5 || role === 15)" class="alert alert-danger mt-2 mx-n2 mb-0 py-2">
            <small>Note: Items for > today are shown because you are not on production environment (local/staging)</small>
        </div>
        <div class="card-body px-0 pb-0 mx-n2 mb-n2">
            <to-do-board-columns 
                ref="boardColumns"
                v-if="selectedDate"
                :role="role" 
                :list-type="selectedListType"
                :date="selectedDate" 
                :consultant-id="selectedConsultant" 
                :user-id="userId"
                :available-height="calculatedAvailableHeight"
                @data-refreshed="refreshUserTabs" 
                @columns-empty-state="onColumnsEmptyStateChange"
            />
        </div>
    </div>
</template>

<script>
import ToDoDateNavigation from './components/ToDoDateNavigation.vue';
import ToDoBoardColumns from './components/ToDoBoardColumns.vue';
import ToDoUserTabs from './components/ToDoUserTabs.vue';
import { getUserInformation } from '../../services/api/to-do';

import '../../../sass/to-do/to-do-list.scss';

export default {
    name: 'ToDo',
    components: { ToDoDateNavigation, ToDoBoardColumns, ToDoUserTabs },
    props: {
        role: {
            type: Number,
            default: 0
        },
        userId: {
            type: Number,
            default: 0,
        },
        userName: {
            type: String,
            default: '',
        },
        position: {
            type: String,
            default: '',
        }
    },
    data() {
        return {
            selectedDate: null, // Will be set after fetching user info
            selectedConsultant: null,
            selectedListType: '',
            isProduction: process.env.NODE_ENV === 'production',
            userInfo: null,
            calculatedAvailableHeight: null,
            isAllColumnsEmpty: false
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.fetchUserInfo();
            this.getListType(this.role);
            this.calculateAvailableHeight();
            window.addEventListener('resize', this.calculateAvailableHeight);
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.calculateAvailableHeight);
    },
    methods: {
        async fetchUserInfo() {
            try {
                const response = await getUserInformation();
                this.userInfo = response.data.data;
                
                const newDate = this.getDefaultDate();
                this.selectedDate = newDate;
            } catch (error) {
                console.error('Error fetching user info:', error);
                this.selectedDate = this.getDefaultDateFallback();
            }
        },
        onDateChange(date) {
            if (date !== this.selectedDate) {
                this.selectedDate = date;
                
                // Recalculate height
                this.$nextTick(() => {
                    this.calculateAvailableHeight();
                });
            }
        },
        onUserTabClick(consultant) {
            if (consultant !== this.selectedConsultant) {
                this.selectedConsultant = consultant;
                this.getListType(this.role);
                
                // Recalculate height
                this.$nextTick(() => {
                    this.calculateAvailableHeight();
                });
            }
        },
        getListType(role_id) {
            if (role_id === 5 || role_id === 15) {
                this.selectedListType = 'team_manager';
            } else {
                this.selectedListType = 'consultant';
            }
        },
        getDefaultDate() {
            if (!this.userInfo?.current_user_datetime) {
                return null;
            }
            
            const dateTimeStr = this.userInfo.current_user_datetime;
            const [datePart, timePart] = dateTimeStr.split(' ');
            const [year, month, day] = datePart.split('-').map(Number);
            const [hour, minute, second] = timePart.split(':').map(Number);
            
            // Create date using local time components
            const userDateTime = new Date(year, month - 1, day, hour, minute, second);
            
            return this.formatDateForRole(userDateTime);
        },
        getDefaultDateFallback() {
            const fallbackDate = new Date();
            return this.formatDateForRole(fallbackDate);
        },
        formatDateForRole(date) {
            let targetDate = new Date(date);
            
            if (this.role === 5 || this.role === 15) {
                targetDate.setDate(targetDate.getDate() - 1);
            }
            
            // Use toLocaleDateString to get the local date parts to avoid timezone issues
            // Use 'en-CA', because it gives YYYY-MM-DD format
            const localDateStr = targetDate.toLocaleDateString('en-CA');
            return localDateStr;
        },
        refreshUserTabs() {
            if ((this.role === 5 || this.role === 15) && this.$refs.userTabs) {
                this.$refs.userTabs.getMyConsultants();

                // Recalculate height
                this.$nextTick(() => {
                    this.calculateAvailableHeight();
                });
            }
        },
        calculateAvailableHeight() {
            this.$nextTick(() => {
                const cardTodoElement = this.$el;
                
                if (!cardTodoElement) {
                    return;
                }

                // Get the actual height of the .card-todo element
                const cardTodoHeight = cardTodoElement.offsetHeight;
                
                // Calculate space used by other elements within .card-todo
                const dateNavigation = cardTodoElement.querySelector('[class*="date"]');
                const userTabs = cardTodoElement.querySelector('[class*="tab"]');
                const alert = cardTodoElement.querySelector('.alert');
                
                // Calculate heights of fixed elements
                const dateNavHeight = dateNavigation ? dateNavigation.offsetHeight : 0;
                const userTabsHeight = userTabs ? userTabs.offsetHeight : 0;
                const alertHeight = alert ? alert.offsetHeight : 0;
                
                // Account for padding and margins
                const cardPadding = 32;
                const columnHeaderHeight = 60;
                const buffer = 20;
                
                // Calculate available height for .col-items-wrapper
                const usedSpace = dateNavHeight + userTabsHeight + alertHeight + cardPadding + columnHeaderHeight + buffer;
                this.calculatedAvailableHeight = Math.max(200, cardTodoHeight - usedSpace);
            });
        },
        onColumnsEmptyStateChange(isEmpty) {
            this.isAllColumnsEmpty = isEmpty;
            
            // Recalculate height when empty state changes
            this.$nextTick(() => {
                this.calculateAvailableHeight();
            });
        }
    }
}
</script>
